import React, {createContext, useContext, useState, useCallback, useMemo, useEffect} from 'react';

const SidebarContext = createContext({
    isCollapsed: false,
    toggleSidebar: () => {},
    sidebarWidth: 240,
    isMobile: false,
    isMobileOpen: false,
    closeMobileSidebar: () => {},
});

const STORAGE_KEY = 'dashbo_sidebar_collapsed';
const BREAKPOINT_MOBILE = 768;
const BREAKPOINT_TABLET = 1100;

export function SidebarProvider({children}) {
    const [isCollapsed, setIsCollapsed] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) === 'true';
        } catch {
            return false;
        }
    });
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < BREAKPOINT_MOBILE);
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            const nowMobile = width < BREAKPOINT_MOBILE;
            const nowTablet = width >= BREAKPOINT_MOBILE && width < BREAKPOINT_TABLET;

            setIsMobile(nowMobile);

            if (nowMobile) {
                setIsMobileOpen(false);
            } else if (nowTablet) {
                setIsCollapsed(true);
            } else {
                try {
                    setIsCollapsed(localStorage.getItem(STORAGE_KEY) === 'true');
                } catch {
                    setIsCollapsed(false);
                }
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleSidebar = useCallback(() => {
        if (isMobile) {
            setIsMobileOpen(prev => !prev);
            return;
        }
        setIsCollapsed(prev => {
            const next = !prev;
            if (window.innerWidth >= BREAKPOINT_TABLET) {
                try {
                    localStorage.setItem(STORAGE_KEY, String(next));
                } catch { /* ignore */ }
            }
            return next;
        });
    }, [isMobile]);

    const closeMobileSidebar = useCallback(() => {
        setIsMobileOpen(false);
    }, []);

    const sidebarWidth = isMobile ? 0 : (isCollapsed ? 56 : 240);

    const value = useMemo(() => ({
        isCollapsed,
        toggleSidebar,
        sidebarWidth,
        isMobile,
        isMobileOpen,
        closeMobileSidebar,
    }), [isCollapsed, toggleSidebar, sidebarWidth, isMobile, isMobileOpen, closeMobileSidebar]);

    return (
        <SidebarContext.Provider value={value}>
            {children}
        </SidebarContext.Provider>
    );
}

export const useSidebar = () => useContext(SidebarContext);
